// ✅ REDEVANCES.JSX CORRIGÉ - PAIEMENT EN CASCADE PAR CLIENT
// Le paiement utilise customer_id et le nouvel endpoint /record-payment-by-customer
import React, { useState, useEffect, useMemo } from 'react';
import { 
  DollarSign, AlertTriangle, CheckCircle, Clock, Send, Eye, 
  Plus, X, MessageSquare, Users, TrendingUp, Calendar, 
  AlertCircle, Phone, ChevronLeft, ChevronRight, Loader, Upload
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGuard } from '../components/PermissionGuard';
import { PERMISSIONS } from '../utils/permissions';
import { api } from '../services/api';
import ExportRedevances from './ExportRedevances';

const Redevances = () => {
  const { hasPermission } = usePermissions();
  
  const [stats, setStats] = useState({
    revenuesMonth: 0,
    expectedRevenues: 0,
    clientsUpToDate: 0,
    totalClients: 0,
    unpaidAmount: 0,
    unpaidCount: 0,
    blockedMeters: 0,
    collectionRate: 0
  });
  
  const [unpaidFees, setUnpaidFees] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showGroupSMSModal, setShowGroupSMSModal] = useState(false);
  const [showCustomMessageModal, setShowCustomMessageModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateFee, setGenerateFee] = useState(null);
  const [generateForm, setGenerateForm] = useState({ start_month: '', start_year: '', end_month: '', end_year: '' });
  
  const [selectedFee, setSelectedFee] = useState(null);
  const [feeDetails, setFeeDetails] = useState(null);
  const [selectedFees, setSelectedFees] = useState([]);
  
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_method: 'cash',
    reference: '',
    auto_unblock: true
  });
  
  const [groupMessage, setGroupMessage] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [searching, setSearching] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchRedevancesData();
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchQuery, statusFilter, riskFilter, currentPage]);

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchQuery, riskFilter, statusFilter]);

  const fetchRedevancesData = async () => {
    try {
      if (searchQuery && searchQuery.trim()) {
        setSearching(true);
      } else {
        setLoading(true);
      }
      
      const feesParams = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchQuery?.trim() || undefined,
        risk_level: riskFilter !== 'all' ? riskFilter : undefined
      };
      
      Object.keys(feesParams).forEach(key => 
        feesParams[key] === undefined && delete feesParams[key]
      );
      
      const feesResponse = await api.get('/monthly-fees/consolidated', { params: feesParams });
      const consolidatedData = feesResponse.data.data || [];
      
      const sorted = [...consolidatedData].sort((a, b) => {
        const dateA = new Date(a.paid_date || a.due_date || '2000-01-01').getTime();
        const dateB = new Date(b.paid_date || b.due_date || '2000-01-01').getTime();
        return dateB - dateA;
      });

      const transformedFees = sorted.map(item => ({
        id: item.fee_id || item.customer_id,
        customer_id: item.customer_id,          // ← clé pour le nouvel endpoint
        customer: {
          full_name: item.customer_name,
          phone: item.customer_phone
        },
        meter: {
          meter_id: item.meter_id,
          status: item.meter_status
        },
        // amount = dette restante totale (ex: 5000 FCFA = 5 mois)
        amount: item.remaining_amount || 0,
        paid_amount: item.total_paid || 0,
        total_amount: item.total_amount || 0,
        status: item.remaining_amount === 0 ? 'paid' : item.status,
        months_overdue: item.months_overdue || 0,
        months_owed: item.months_owed || 0,     // ← nb de mois impayés
        month: item.oldest_month,
        year: item.oldest_year,
        due_date: item.due_date,
        paid_date: item.paid_date,
        zone: item.zone
      }));
      
      setUnpaidFees(transformedFees);
      setTotalRecords(feesResponse.data.total || 0);

      if (currentPage === 1) {
        const statsResponse = await api.get('/monthly-fees/stats/summary');
        setStats(statsResponse.data);
      }

    } catch (error) {
      console.error('Erreur chargement redevances:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handleViewDetails = async (fee) => {
    try {
      setSubmitting(true);
      if (!fee.id || fee.id === fee.customer_id) {
        setFeeDetails({
          customer: fee.customer,
          meter: fee.meter,
          month: fee.month,
          year: fee.year,
          amount: fee.total_amount || 0,
          paid_amount: fee.paid_amount || 0,
          remaining_amount: fee.amount || 0,
          status: fee.status,
          due_date: fee.due_date,
          paid_date: fee.paid_date,
          months_overdue: fee.months_overdue || 0,
          months_owed: fee.months_owed || 0
        });
        setShowDetailsModal(true);
      } else {
        const response = await api.get(`/monthly-fees/${fee.id}`);
        setFeeDetails({
          ...response.data,
          amount: fee.total_amount || response.data.amount,
          paid_amount: fee.paid_amount || response.data.paid_amount,
          remaining_amount: fee.amount || response.data.remaining_amount,
          months_overdue: fee.months_overdue || response.data.months_overdue,
          months_owed: fee.months_owed || 0
        });
        setShowDetailsModal(true);
      }
    } catch (error) {
      console.error('Erreur détails:', error);
      toast.error('Erreur lors du chargement des détails');
    } finally {
      setSubmitting(false);
    }
  };
  
  // ✅ CORRIGÉ : pré-remplit avec la dette totale et garde customer_id
  const handleRecordPayment = (fee) => {
    if (!hasPermission(PERMISSIONS.RECORD_PAYMENT)) {
      toast.error('Vous n\'avez pas la permission d\'enregistrer des paiements');
      return;
    }
    setSelectedFee(fee);
    setPaymentForm({
      amount: fee.amount,           // dette totale restante
      payment_method: 'cash',
      reference: '',
      auto_unblock: true
    });
    setShowPaymentModal(true);
  };

  // ✅ CORRIGÉ : utilise /record-payment-by-customer avec customer_id
  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!hasPermission(PERMISSIONS.RECORD_PAYMENT)) {
      toast.error('Permission refusée');
      return;
    }
    if (!selectedFee) return;

    try {
      setSubmitting(true);

      // ✅ Endpoint en cascade : paie du plus vieux au plus récent
      const response = await api.post('/monthly-fees/record-payment', {
        fee_id: selectedFee.id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        reference: paymentForm.reference || null,
        auto_unblock: paymentForm.auto_unblock
      });

      const { months_paid, months_remaining, remaining_debt, meter_unblocked } = response.data;

      if (meter_unblocked) {
        toast.success(
          `✅ ${months_paid} mois soldés ! Compteur débloqué automatiquement. Il reste ${months_remaining} mois (${formatCurrency(remaining_debt)}).`,
          { autoClose: 6000 }
        );
      } else if (months_remaining === 0) {
        toast.success(`✅ ${months_paid} mois soldés ! Client à jour.`, { autoClose: 5000 });
      } else {
        toast.success(
          `✅ ${months_paid} mois soldés. Il reste ${months_remaining} mois impayé${months_remaining > 1 ? 's' : ''} (${formatCurrency(remaining_debt)}).`,
          { autoClose: 5000 }
        );
      }

      setShowPaymentModal(false);
      setSelectedFee(null);
      fetchRedevancesData();
    } catch (error) {
      console.error('Erreur paiement:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendCustomMessage = (fee) => {
    setSelectedFee(fee);
    setCustomMessage('');
    setShowCustomMessageModal(true);
  };

  const handleSubmitCustomMessage = async (e) => {
    e.preventDefault();
    if (!selectedFee || !customMessage.trim()) return;
    try {
      setSubmitting(true);
      await api.post(`/monthly-fees/${selectedFee.id}/send-custom-message`, {
        fee_id: selectedFee.id,
        message: customMessage
      });
      toast.success(`Message envoyé avec succès`);
      setShowCustomMessageModal(false);
      setCustomMessage('');
    } catch (error) {
      console.error('Erreur SMS:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendGroupSMS = async (e) => {
    e.preventDefault();
    if (!groupMessage.trim()) {
      toast.warn('Veuillez saisir un message');
      return;
    }
    try {
      setSubmitting(true);
      const response = await api.post('/monthly-fees/send-group-sms', {
        message: groupMessage,
        fee_ids: selectedFees.length > 0 ? selectedFees : null
      });
      toast.success(`SMS envoyé à ${response.data.count} clients !`);
      setShowGroupSMSModal(false);
      setGroupMessage('');
      setSelectedFees([]);
    } catch (error) {
      console.error('Erreur SMS groupé:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi des SMS');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReminders = async () => {
    if (!hasPermission(PERMISSIONS.RECORD_PAYMENT)) {
      toast.error('Vous n\'avez pas la permission d\'envoyer des rappels');
      return;
    }
    if (!window.confirm('Envoyer des rappels SMS à tous les clients avec redevances impayées ?')) return;
    try {
      setSubmitting(true);
      const response = await api.post('/monthly-fees/send-reminders');
      toast.success(`Rappels SMS envoyés avec succès à ${response.data.count} clients !`);
    } catch (error) {
      console.error('Erreur rappels:', error);
      toast.error('Erreur lors de l\'envoi des rappels');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Générer mois manquants ───
  const handleGenerateMissingFees = (fee) => {
    setGenerateFee(fee);
    const now = new Date();
    setGenerateForm({
      start_month: fee.month || now.getMonth() + 1,
      start_year: fee.year || now.getFullYear(),
      end_month: now.getMonth() + 1,
      end_year: now.getFullYear()
    });
    setShowGenerateModal(true);
  };

  const handleSubmitGenerateFees = async (e) => {
    e.preventDefault();
    if (!generateFee) return;
    try {
      setSubmitting(true);
      const response = await api.post('/monthly-fees/generate-missing', {
        meter_id: generateFee.meter?.meter_id,
        start_month: parseInt(generateForm.start_month),
        start_year: parseInt(generateForm.start_year),
        end_month: parseInt(generateForm.end_month),
        end_year: parseInt(generateForm.end_year)
      });
      const { created, skipped } = response.data;
      toast.success(`✅ ${created} mois générés${skipped > 0 ? `, ${skipped} déjà existants ignorés` : ''}`, { autoClose: 5000 });
      setShowGenerateModal(false);
      setGenerateFee(null);
      fetchRedevancesData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Helpers ───
  const getMonthsOverdue = (fee) => fee.months_overdue || 0;

  const getStatusBadge = (fee) => {
    const monthsOverdue = getMonthsOverdue(fee);
    if (fee.amount === 0 || fee.status === 'paid') {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 font-medium">✓ À jour</span>;
    }
    if (monthsOverdue >= 4) return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 font-medium">🔴 Critique ({monthsOverdue} mois)</span>;
    if (monthsOverdue === 3) return <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700 font-medium">⚠️ Alerte ({monthsOverdue} mois)</span>;
    if (monthsOverdue >= 1) return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 font-medium">📅 Retard ({monthsOverdue} mois)</span>;
    return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 font-medium">✅ Récent</span>;
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('fr-FR', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount) + ' FCFA';

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getMonthName = (month) => {
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return months[month - 1] || '';
  };

  const toggleFeeSelection = (feeId) =>
    setSelectedFees(prev => prev.includes(feeId) ? prev.filter(id => id !== feeId) : [...prev, feeId]);

  const toggleSelectAll = () =>
    setSelectedFees(selectedFees.length === unpaidFees.length ? [] : unpaidFees.map(f => f.id));

  // ✅ Calcul du résumé dans le modal de paiement
  const getPaymentSummary = () => {
    if (!selectedFee || !paymentForm.amount) return null;
    const amountEntered = parseFloat(paymentForm.amount) || 0;
    const monthlyRate = 1000; // FCFA par mois
    const monthsPaid = Math.floor(amountEntered / monthlyRate);
    const remainder = amountEntered % monthlyRate;
    const monthsRemaining = Math.max(0, (selectedFee.months_owed || 0) - monthsPaid);
    const debtRemaining = Math.max(0, (selectedFee.amount || 0) - amountEntered);
    return { monthsPaid, remainder, monthsRemaining, debtRemaining };
  };

  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const goToFirstPage = () => { if (currentPage !== 1) setCurrentPage(1); };
  const goToLastPage  = () => { if (currentPage !== totalPages) setCurrentPage(totalPages); };
  const goToNextPage  = () => { if (currentPage < totalPages) setCurrentPage(prev => prev + 1); };
  const goToPrevPage  = () => { if (currentPage > 1) setCurrentPage(prev => prev - 1); };
  const goToPage      = (n) => setCurrentPage(n);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 1) return [];
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end   = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  if (loading && currentPage === 1) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  const paymentSummary = getPaymentSummary();

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-slate-50 to-blue-50">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ── EN-TÊTE ── */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Redevances</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <PermissionGuard permission={PERMISSIONS.RECORD_PAYMENT}>
              <button onClick={() => setShowGroupSMSModal(true)} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />SMS Groupé
              </button>
              <button onClick={handleSendReminders} disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:bg-blue-400">
                <Send className="w-4 h-4" />Rappels Auto
              </button>
              <ExportRedevances />
            </PermissionGuard>
          </div>
        </div>

        {/* ── STATISTIQUES ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm font-medium">Redevances du Mois</span>
              <DollarSign className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">{formatCurrency(stats.revenuesMonth || 0)}</div>
            <p className="text-xs text-gray-500">sur {formatCurrency(stats.expectedRevenues || 0)} attendu</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm font-medium">Clients à Jour</span>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">{stats.clientsUpToDate || 0}</div>
            <p className="text-xs text-gray-500">
              {stats.totalClients > 0 ? `${Math.round((stats.clientsUpToDate / stats.totalClients) * 100)}%` : '0%'} des clients
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm font-medium">Impayés</span>
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="text-2xl font-bold text-red-600 mb-1">{formatCurrency(stats.unpaidAmount || 0)}</div>
            <p className="text-xs text-gray-500">{stats.unpaidCount || 0} client{stats.unpaidCount > 1 ? 's' : ''} en retard</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm font-medium">Compteurs Bloqués</span>
              <Clock className="w-5 h-5 text-red-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">{stats.blockedMeters || 0}</div>
            <p className="text-xs text-gray-500">Statut: Suspendu</p>
          </div>
        </div>

        {/* ── FILTRES ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative">
              <label className="block text-xs font-medium text-gray-600 mb-1">Recherche</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nom du client ou N° compteur..."
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                autoComplete="off"
              />
              {searching && <div className="absolute right-3 top-9"><Loader className="w-4 h-4 animate-spin text-blue-600" /></div>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Statut</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="all">Tous</option>
                <option value="unpaid">Impayés</option>
                <option value="paid">Payés</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Niveau de risque</label>
              <select value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="all">Tous</option>
                <option value="critical">Critique (4+ mois)</option>
                <option value="alert">Alerte (3 mois)</option>
                <option value="warning">Retard (1-2 mois)</option>
                <option value="recent">Récent</option>
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={fetchRedevancesData} className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Actualiser
              </button>
            </div>
          </div>
        </div>

        {/* ── TABLEAU ── */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                Redevances
                <span className="ml-2 text-sm font-normal text-gray-500">({totalRecords})</span>
              </h2>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input type="checkbox" checked={selectedFees.length === unpaidFees.length && unpaidFees.length > 0} onChange={toggleSelectAll} className="rounded border-gray-300" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Compteur</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant Dû</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mois impayés</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <PermissionGuard permissions={[PERMISSIONS.RECORD_PAYMENT, PERMISSIONS.VIEW_FEES]}>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </PermissionGuard>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
                      <p className="text-sm text-gray-500 mt-2">Chargement...</p>
                    </td>
                  </tr>
                ) : unpaidFees.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                      <p className="text-lg font-medium text-gray-900">Aucune redevance</p>
                    </td>
                  </tr>
                ) : (
                  unpaidFees.map((fee) => {
                    const monthsOwed = fee.months_owed || 0;
                    return (
                      <tr key={fee.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <input type="checkbox" checked={selectedFees.includes(fee.id)} onChange={() => toggleFeeSelection(fee.id)} className="rounded border-gray-300" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
                              <span className="text-white font-semibold text-sm">
                                {fee.customer?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'N/A'}
                              </span>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">{fee.customer?.full_name || 'N/A'}</div>
                              <div className="text-xs text-gray-500 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {fee.customer?.phone || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 font-mono font-medium">{fee.meter?.meter_id || 'N/A'}</span>
                          {fee.meter?.status === 'suspended' && <div className="text-xs text-red-600 font-medium mt-1">🔒 Bloqué</div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {fee.status === 'paid' && fee.paid_date ? (
                            <>
                              <div className="text-sm font-medium text-green-700">✓ Payé le</div>
                              <div className="text-xs text-gray-600">{formatDate(fee.paid_date)}</div>
                            </>
                          ) : fee.month && fee.year ? (
                            <>
                              <div className="text-sm font-medium text-gray-900">{getMonthName(fee.month)} {fee.year}</div>
                              <div className="text-xs text-gray-500">Échéance: {formatDate(fee.due_date)}</div>
                            </>
                          ) : (
                            <div className="text-sm text-gray-500">Vue consolidée ({monthsOwed} mois)</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-900">{formatCurrency(Math.max(0, fee.amount))}</div>
                          {fee.paid_amount > 0 && <div className="text-xs text-green-600">Payé: {formatCurrency(fee.paid_amount)}</div>}
                        </td>
                        {/* ✅ CORRIGÉ : affiche months_owed (mois impayés) */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-semibold ${monthsOwed >= 3 ? 'text-red-600' : monthsOwed >= 1 ? 'text-yellow-600' : 'text-gray-600'}`}>
                            {fee.status === 'paid' ? '0 mois' : `${monthsOwed} mois`}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(fee)}</td>
                        <PermissionGuard permissions={[PERMISSIONS.RECORD_PAYMENT, PERMISSIONS.VIEW_FEES]}>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-2">
                              <PermissionGuard permission={PERMISSIONS.RECORD_PAYMENT}>
                                <button onClick={() => handleRecordPayment(fee)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition" title="Enregistrer paiement">
                                  <DollarSign className="w-4 h-4" />
                                </button>
                              </PermissionGuard>
                              <button onClick={() => handleSendCustomMessage(fee)} className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition" title="Envoyer message">
                                <MessageSquare className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleViewDetails(fee)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition" title="Voir détails">
                                <Eye className="w-4 h-4" />
                              </button>
                              <PermissionGuard permission={PERMISSIONS.GENERATE_FEES}>
                                <button onClick={() => handleGenerateMissingFees(fee)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition" title="Générer mois manquants">
                                  <Plus className="w-4 h-4" />
                                </button>
                              </PermissionGuard>
                            </div>
                          </td>
                        </PermissionGuard>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION ── */}
          {totalRecords > 0 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-sm text-gray-700 shrink-0">
                  Affichage de{' '}
                  <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> à{' '}
                  <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalRecords)}</span> sur{' '}
                  <span className="font-medium">{totalRecords.toLocaleString()}</span>
                  {totalPages > 1 && (
                    <> &nbsp;•&nbsp; Page <span className="font-medium">{currentPage}</span> / <span className="font-medium">{totalPages}</span></>
                  )}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1 flex-wrap justify-center">
                    <button onClick={goToFirstPage} disabled={currentPage === 1} title="Première page"
                      className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center">
                      <ChevronLeft className="w-3 h-3" /><ChevronLeft className="w-3 h-3 -ml-1" />
                    </button>
                    <button onClick={goToPrevPage} disabled={currentPage === 1}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1">
                      <ChevronLeft className="w-4 h-4" />Préc.
                    </button>
                    {pageNumbers.map((page, idx) =>
                      typeof page === 'number' ? (
                        <button key={`p-${page}`} onClick={() => goToPage(page)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentPage === page ? 'bg-blue-600 text-white shadow-sm' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'}`}>
                          {page}
                        </button>
                      ) : (
                        <span key={`e-${idx}`} className="px-1 text-gray-400 select-none text-sm">…</span>
                      )
                    )}
                    <button onClick={goToNextPage} disabled={currentPage === totalPages}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1">
                      Suiv.<ChevronRight className="w-4 h-4" />
                    </button>
                    <button onClick={goToLastPage} disabled={currentPage === totalPages} title="Dernière page"
                      className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center">
                      <ChevronRight className="w-3 h-3" /><ChevronRight className="w-3 h-3 -ml-1" />
                    </button>
                    <div className="flex items-center gap-1.5 ml-2">
                      <span className="text-xs text-gray-500">Aller à</span>
                      <input type="number" min={1} max={totalPages} defaultValue={currentPage} key={currentPage}
                        onBlur={(e) => { const val = parseInt(e.target.value, 10); if (!isNaN(val) && val >= 1 && val <= totalPages) goToPage(val); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { const val = parseInt(e.target.value, 10); if (!isNaN(val) && val >= 1 && val <= totalPages) goToPage(val); }}}
                        className="w-14 px-2 py-1 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ==================== MODALS ==================== */}

        {/* Modal SMS Groupé */}
        {showGroupSMSModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-purple-600" />Envoyer SMS Groupé
                </h3>
                <button onClick={() => { setShowGroupSMSModal(false); setGroupMessage(''); }} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-600">Destinataires:</span>
                    <span className="font-bold text-purple-700">
                      {selectedFees.length > 0 ? `${selectedFees.length} client(s) sélectionné(s)` : `Tous les impayés (${unpaidFees.filter(f => f.status === 'unpaid').length})`}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">{selectedFees.length === 0 && 'Le message sera envoyé à tous les clients avec redevances impayées'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea required value={groupMessage} onChange={(e) => setGroupMessage(e.target.value)} rows="5" maxLength="160"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Exemple: Cher client, veuillez régulariser votre redevance mensuelle. Merci." />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">{groupMessage.length}/160 caractères</p>
                    {groupMessage.length > 140 && <span className="text-xs text-orange-600">Approche de la limite</span>}
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={() => { setShowGroupSMSModal(false); setGroupMessage(''); }} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Annuler</button>
                  <button onClick={handleSendGroupSMS} disabled={submitting || !groupMessage.trim()}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-purple-400 flex items-center justify-center gap-2">
                    {submitting ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Envoi...</> : <><Send className="w-4 h-4" />Envoyer SMS</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Modal Paiement Manuel - CORRIGÉ avec résumé en temps réel */}
        {showPaymentModal && selectedFee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Enregistrer Paiement Redevance</h3>
                <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
              
              <div className="p-6 space-y-4 overflow-y-auto">
                {/* Résumé client */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Client:</span>
                    <span className="font-medium">{selectedFee.customer?.full_name}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Compteur:</span>
                    <span className="font-mono font-medium">{selectedFee.meter?.meter_id}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Mois impayés :</span>
                    <span className="font-bold text-red-600">{selectedFee.months_owed} mois</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Dette totale :</span>
                    <span className="font-bold text-red-600">{formatCurrency(selectedFee.amount)}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Montant payé (FCFA) *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    max={selectedFee.amount}
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Entrez le montant"
                  />
                </div>

                {/* ✅ Résumé en temps réel : mois soldés / mois restants */}
                {paymentSummary && parseFloat(paymentForm.amount) > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1.5">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Récapitulatif</p>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Mois qui seront soldés :</span>
                      <span className="font-bold text-green-600">{paymentSummary.monthsPaid} mois</span>
                    </div>
                    {paymentSummary.remainder > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Paiement partiel sur 1 mois :</span>
                        <span className="font-medium text-orange-600">{formatCurrency(paymentSummary.remainder)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm border-t border-gray-200 pt-1.5 mt-1.5">
                      <span className="text-gray-600">Mois restants après paiement :</span>
                      <span className={`font-bold ${paymentSummary.monthsRemaining === 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {paymentSummary.monthsRemaining} mois
                        {paymentSummary.monthsRemaining === 0 ? ' ✓' : ''}
                      </span>
                    </div>
                    {paymentSummary.debtRemaining > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Dette restante :</span>
                        <span className="font-bold text-red-600">{formatCurrency(paymentSummary.debtRemaining)}</span>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mode de Paiement *</label>
                  <select value={paymentForm.payment_method} onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="cash">Espèces</option>
                    <option value="wave">Wave</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="bank_transfer">Virement Bancaire</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Référence Paiement</label>
                  <input type="text" value={paymentForm.reference} onChange={(e) => setPaymentForm({...paymentForm, reference: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Numéro de transaction (optionnel)" />
                </div>

                <div className="flex items-center">
                  <input type="checkbox" id="auto_unblock" checked={paymentForm.auto_unblock}
                    onChange={(e) => setPaymentForm({...paymentForm, auto_unblock: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                  <label htmlFor="auto_unblock" className="ml-2 text-sm text-gray-700">
                    Débloquer automatiquement le compteur si toutes les redevances sont payées
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button onClick={() => setShowPaymentModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Annuler</button>
                  <button onClick={handleSubmitPayment} disabled={submitting || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400">
                    {submitting ? 'Enregistrement...' : 'Enregistrer le Paiement'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Message Personnalisé */}
        {showCustomMessageModal && selectedFee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-purple-600" />Envoyer Message SMS
                </h3>
                <button onClick={() => setShowCustomMessageModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4 overflow-y-auto">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Client:</span>
                    <span className="font-medium">{selectedFee.customer?.full_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Téléphone:</span>
                    <span className="font-medium">{selectedFee.customer?.phone || 'N/A'}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea required value={customMessage} onChange={(e) => setCustomMessage(e.target.value)} rows="4" maxLength="160"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Tapez votre message ici..." />
                  <p className="text-xs text-gray-500 mt-1">{customMessage.length}/160 caractères</p>
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={() => setShowCustomMessageModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Annuler</button>
                  <button onClick={handleSubmitCustomMessage} disabled={submitting}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-purple-400">
                    {submitting ? 'Envoi...' : 'Envoyer SMS'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Détails Redevance */}
        {showDetailsModal && feeDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
                <h3 className="text-lg font-semibold text-gray-900">Détails de la Redevance</h3>
                <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <div className="w-1 h-4 bg-blue-600 rounded"></div>Informations Client
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Nom complet:</span><span className="text-sm font-medium">{feeDetails.customer?.full_name || 'N/A'}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Téléphone:</span><span className="text-sm font-medium">{feeDetails.customer?.phone || 'N/A'}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Email:</span><span className="text-sm font-medium">{feeDetails.customer?.email || 'N/A'}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Adresse:</span><span className="text-sm font-medium text-right">{feeDetails.customer?.address || 'N/A'}</span></div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <div className="w-1 h-4 bg-green-600 rounded"></div>Informations Compteur
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Numéro compteur:</span><span className="text-sm font-mono font-medium">{feeDetails.meter?.meter_id || 'N/A'}</span></div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Statut:</span>
                      <span className={`text-sm font-medium ${feeDetails.meter?.status === 'active' ? 'text-green-600' : feeDetails.meter?.status === 'suspended' ? 'text-red-600' : 'text-gray-600'}`}>
                        {feeDetails.meter?.status === 'active' ? '✅ Actif' : feeDetails.meter?.status === 'suspended' ? '🔒 Suspendu' : feeDetails.meter?.status || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Departement:</span><span className="text-sm font-medium">{feeDetails.meter?.zone || 'N/A'}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Solde actuel:</span><span className="text-sm font-medium">{feeDetails.meter?.current_balance || 0} kWh</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Redevance mensuelle:</span><span className="text-sm font-medium">{formatCurrency(feeDetails.meter?.monthly_fee || 0)}</span></div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <div className="w-1 h-4 bg-yellow-600 rounded"></div>Détails du Paiement
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    {feeDetails.month && feeDetails.year ? (
                      <div className="flex justify-between"><span className="text-sm text-gray-600">Période:</span><span className="text-sm font-medium">{getMonthName(feeDetails.month)} {feeDetails.year}</span></div>
                    ) : (
                      <div className="flex justify-between"><span className="text-sm text-gray-600">Période:</span><span className="text-sm font-medium text-blue-600">Vue consolidée ({feeDetails.months_owed || 0} mois)</span></div>
                    )}
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Montant total:</span><span className="text-sm font-bold">{formatCurrency(feeDetails.amount)}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Montant payé:</span><span className="text-sm font-medium text-green-600">{formatCurrency(feeDetails.paid_amount)}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Reste à payer:</span><span className="text-sm font-bold text-red-600">{formatCurrency(feeDetails.remaining_amount)}</span></div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Statut:</span>
                      <span className={`text-sm font-medium ${feeDetails.status === 'paid' ? 'text-green-600' : 'text-red-600'}`}>
                        {feeDetails.status === 'paid' ? '✓ Payé' : '✗ Impayé'}
                      </span>
                    </div>
                    {feeDetails.months_owed > 0 && (
                      <div className="flex justify-between"><span className="text-sm text-gray-600">Nombre de mois dus:</span><span className="text-sm font-bold text-orange-600">{feeDetails.months_owed} mois</span></div>
                    )}
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Date d'échéance:</span><span className="text-sm font-medium">{formatDate(feeDetails.due_date)}</span></div>
                    {feeDetails.paid_date && <div className="flex justify-between"><span className="text-sm text-gray-600">Date de paiement:</span><span className="text-sm font-medium">{formatDate(feeDetails.paid_date)}</span></div>}
                    {feeDetails.payment_method && <div className="flex justify-between"><span className="text-sm text-gray-600">Mode de paiement:</span><span className="text-sm font-medium">{feeDetails.payment_method}</span></div>}
                    {feeDetails.payment_reference && <div className="flex justify-between"><span className="text-sm text-gray-600">Référence:</span><span className="text-sm font-mono">{feeDetails.payment_reference}</span></div>}
                  </div>
                </div>
                <div className="p-6 pt-4 border-t border-gray-200 flex gap-3 shrink-0">
              <button onClick={() => setShowPaymentModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Annuler</button>
              <button onClick={handleSubmitPayment} disabled={submitting || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400">
                {submitting ? 'Enregistrement...' : 'Enregistrer le Paiement'}
              </button>
            </div>
              </div>
            </div>
          </div>
        )}

      {/* ✅ Modal Générer Mois Manquants */}
        {showGenerateModal && generateFee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-green-600" />
                  Générer Mois Manquants
                </h3>
                <button onClick={() => setShowGenerateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">

                {/* Info compteur */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Client :</span>
                    <span className="font-medium">{generateFee.customer?.full_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Compteur :</span>
                    <span className="font-mono font-medium">{generateFee.meter?.meter_id}</span>
                  </div>
                </div>

                <p className="text-xs text-gray-500">
                  Les mois déjà existants seront ignorés automatiquement (anti-doublon).
                  Chaque mois généré = 1 000 FCFA de redevance impayée.
                </p>

                {/* Mois de début */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mois de début</label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={generateForm.start_month}
                      onChange={(e) => setGenerateForm({...generateForm, start_month: e.target.value})}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'].map((m, i) => (
                        <option key={i+1} value={i+1}>{m}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="2020" max="2030"
                      value={generateForm.start_year}
                      onChange={(e) => setGenerateForm({...generateForm, start_year: e.target.value})}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Année"
                    />
                  </div>
                </div>

                {/* Mois de fin */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mois de fin</label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={generateForm.end_month}
                      onChange={(e) => setGenerateForm({...generateForm, end_month: e.target.value})}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'].map((m, i) => (
                        <option key={i+1} value={i+1}>{m}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="2020" max="2030"
                      value={generateForm.end_year}
                      onChange={(e) => setGenerateForm({...generateForm, end_year: e.target.value})}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Année"
                    />
                  </div>
                </div>

                {/* Résumé */}
                {generateForm.start_month && generateForm.start_year && generateForm.end_month && generateForm.end_year && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Récapitulatif</p>
                    {(() => {
                      const months = (parseInt(generateForm.end_year) - parseInt(generateForm.start_year)) * 12
                        + (parseInt(generateForm.end_month) - parseInt(generateForm.start_month)) + 1;
                      return months > 0 ? (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Mois à générer :</span>
                          <span className="font-bold text-green-600">{months} mois = {formatCurrency(months * 1000)}</span>
                        </div>
                      ) : (
                        <p className="text-xs text-red-500">La date de fin doit être après la date de début</p>
                      );
                    })()}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowGenerateModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                    Annuler
                  </button>
                  <button
                    onClick={handleSubmitGenerateFees}
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400 flex items-center justify-center gap-2"
                  >
                    {submitting ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Génération...</> : <><Plus className="w-4 h-4" />Générer</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Redevances;